import { useState, useEffect, useRef } from 'react';
import { AES, enc, mode, lib } from 'crypto-js';
import {
	checkEmailFormat,
	passwordStrengthValidation,
	checkNameFormat,
	isNumeric
} from '../../utils/validations';
import { accountAPI } from '../../services/redwolfSdkAPI';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useRecaptcha } from '../common/hooks/useRecaptcha';
import { useGoogleAnalytics } from '../common/hooks/useGoogleAnalytics';
import { addClasses } from '../../utils/eventListeners';
import { useDispatch } from 'react-redux';
import { setTermsOfServiceStatus, setUserProfile, UserProfile } from '../../store/userSlice';
import useTechnicalErrorMessage from '../common/hooks/useTechnicalErrorMessage';
import useHeadingFocuser from '../common/hooks/useHeadingFocuser';
import Spinner from '../common/components/Spinner';
import * as Sentry from '@sentry/react';
import { SocialResponse } from '../common/interfaces/user';
import useRequestRedwolfService from '../common/hooks/useRequestRedwolfService';
import { setLocalStorage, setToken } from '../../utils/localStorage';
import { setSession } from '../../utils/session';
import { openNotification } from '../../store/commonSlice';

type ISignInType = 'TRADITIONAL' | 'google' | 'facebook' | 'apple';

interface RegistrationPayload {
	firstName?: string | null;
	lastName?: string | null;
	email: string;
	postalCode?: string | null;
	phoneNumber?: string | null;
	password?: string;
	emailOptin: boolean;
	facebookToken?: string;
	googleToken?: string;
	appleToken?: string;
	reCaptchaToken?: string;
}

interface QueryParams {
	firstName: string;
	lastName: string;
	email: string;
	zipCode: string;
}

const getQueryParams = (): QueryParams => {
	const result: QueryParams = {
		firstName: '',
		lastName: '',
		email: '',
		zipCode: ''
	};

	const searchParams = new URLSearchParams(window.location.search);
	const value = searchParams.get('d');

	if (value != null) {
		const cipherParams = lib.CipherParams.create({ ciphertext: enc.Hex.parse(value) });
		const privateKey = enc.Utf8.parse('80AA3AE1FA23387744AA94AD8FAC8715');
		const initializationVector = enc.Utf8.parse('H0zUFLRQu3v603DP');
		const wordArray = AES.decrypt(cipherParams, privateKey, {
			iv: initializationVector,
			mode: mode.CBC
		});
		const params = new URLSearchParams(enc.Utf8.stringify(wordArray));
		const timestamp = params.get('ts');

		if (timestamp != null) {
			const expirationDate = new Date(timestamp);
			expirationDate.setDate(expirationDate.getDate() + 1);

			if (new Date() < expirationDate) {
				result.firstName = params.get('fn') as string;
				result.lastName = params.get('ln') as string;
				result.zipCode = params.get('zc') as string;
				result.email = params.get('ea') as string;
			}
		}
	}

	return result;
};

const SignUp = () => {
	const navigate = useNavigate();
	const location: any = useLocation();
	const dispatch = useDispatch();
	const queryParams = getQueryParams();
	const [registrationForm, setRegistrationForm] = useState({
		firstName: queryParams.firstName,
		lastName: queryParams.lastName,
		phoneNumber: '',
		zipCode: queryParams.zipCode,
		email: queryParams.email,
		password: '',
		emailOptin: false,
		isSMSConsentChecked: false,
		isSMSConsentDisabled: true
	});
	const [formErrorMessages, setFormErrorMessages] = useState({
		firstName: '',
		lastName: '',
		phone: '',
		zipCode: '',
		email: '',
		password: ''
	});
	const [passwordValid, setPasswordValid] = useState({
		isAllCasesPassed: false,
		isPasswordLengthPassed: false,
		isLowercasePassed: false,
		isUppercasePassed: false,
		isNumerPassed: false
	});

	const [signUpType, setSignUpType] = useState<ISignInType>('TRADITIONAL');
	const [isPasswordFieldVisible, setIsPasswordFieldVisible] = useState(true);
	const [isEmailDisabled, setIsEmailDisabled] = useState(false);
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
	const [isPasswordTooltipOpened, setIsPasswordTooltipOpened] = useState(false);
	const [isEmailAlreadyInUse, setIsEmailAlreadyInUse] = useState(false);
	const [isCreateAccountButtonClicked, setIsCreateAccountButtonClicked] = useState(true);
	const [isFormInputValid, setIsFormInputValid] = useState(false);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [isRecaptchaLoading, generateRecaptchaToken]: any = useRecaptcha();
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();

	const [headingRef] = useHeadingFocuser();

	const firstNameRef = useRef<HTMLInputElement>(null);
	const lastNameRef = useRef<HTMLInputElement>(null);
	const phoneRef = useRef<HTMLInputElement>(null);
	const zipCodeRef = useRef<HTMLInputElement>(null);
	const emailRef = useRef<HTMLInputElement>(null);
	const passwordRef = useRef<HTMLInputElement>(null);

	const autoPopulateEmail = location.state?.email;
	const socialResponse: SocialResponse = location.state?.socialResponse;
	const isSocialLogin = location.state?.isSocialLogin;
	const queryStrings = new URLSearchParams(location.search);

	const transaction = Sentry.startInactiveSpan({ name: 'Sign Up' });

	useEffect(() => {
		if (isSocialLogin) {
			logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_VERIFY_ACCOUNT');
			if (socialResponse) {
				setRegistrationForm({
					...registrationForm,
					firstName: socialResponse.firstName,
					lastName: socialResponse.lastName,
					email:
						socialResponse.email &&
						(socialResponse.email.includes('no-email-provided') || !socialResponse.email)
							? ''
							: socialResponse.email
				});
				const token = location.state?.token;
				setSession('verifyAccountToken', token);
				setIsPasswordFieldVisible(false);
				setSignUpType(socialResponse.type);
				if (socialResponse.email) {
					if (!socialResponse.email.includes('no-email-provided')) {
						setIsEmailDisabled(true);
					}
				}
			}
		} else {
			logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_AUTH', { item_title: 'create_account' });
		}

		return () => {
			window.history.replaceState(null, '');
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		addClasses();

		if (autoPopulateEmail) {
			setRegistrationForm({ ...registrationForm, email: autoPopulateEmail });
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setIsSpinnerLoading(!isRecaptchaLoading);
	}, [isRecaptchaLoading]);

	const handleSMSConsentChange = (event: any) => {
		setRegistrationForm({ ...registrationForm, isSMSConsentChecked: event.target.checked });
	};

	const handleKeyDown = (event: any) => {
		const { name, value, selectionStart, selectionEnd } = event.target;
		const RegexNumberSpace = /^[0-9\s]+$/i;
		const RegexNumber = /^[0-9]+$/i;

		if ((name === 'firstName' || name === 'lastName') && !checkNameFormat(event.key)) {
			event.preventDefault();
		} else if (
			name === 'phoneNumber' &&
			event.keyCode === 8 &&
			'-() '.includes(value[selectionStart - 1]) &&
			value[selectionStart]
		) {
			phoneRef.current &&
				phoneRef.current.setSelectionRange(selectionStart - 1, selectionStart - 1);
			event.preventDefault();
		} else if (
			name === 'phoneNumber' &&
			event.keyCode === 8 &&
			'-() '.includes(value[selectionStart]) &&
			RegexNumberSpace.test(value[selectionEnd])
		) {
			phoneRef.current && phoneRef.current.setSelectionRange(selectionStart, selectionStart);
			event.preventDefault();
		} else if (
			name === 'phoneNumber' &&
			'-() '.includes(value[selectionStart]) &&
			RegexNumber.test(value[selectionEnd])
		) {
			phoneRef.current && phoneRef.current.setSelectionRange(selectionStart, selectionStart);
			event.preventDefault();
		}
	};

	const handleInputChange = (event: any) => {
		const { name, value } = event.target;

		const registration = { ...registrationForm, [name]: value };
		setFormErrorMessages({ ...formErrorMessages, [name]: '' });
		setIsEmailAlreadyInUse(false);

		if (name === 'password') {
			const passwordValid = passwordStrengthValidation(value);
			setPasswordValid(passwordValid);
		}
		if (name === 'phoneNumber' && value.length < 15) {
			setFormErrorMessages({ ...formErrorMessages, phone: '' });

			let formattedPhoneNumber = value.replace(/[^\d]/g, '');
			const cursorPosition = event.target.selectionStart;

			if (formattedPhoneNumber.length === 0) {
				formattedPhoneNumber = '';
			} else if (formattedPhoneNumber.length > 0 && formattedPhoneNumber.length < 4) {
				formattedPhoneNumber = `(${formattedPhoneNumber}`;
			} else if (formattedPhoneNumber.length > 3 && formattedPhoneNumber.length < 7) {
				formattedPhoneNumber = `(${formattedPhoneNumber.slice(0, 3)}) ${formattedPhoneNumber.slice(
					3,
					6
				)}`;
			} else if (formattedPhoneNumber.length >= 7) {
				formattedPhoneNumber = `(${formattedPhoneNumber.slice(0, 3)}) ${formattedPhoneNumber.slice(
					3,
					6
				)}-${formattedPhoneNumber.slice(6, 10)}`;
			}

			if (value[cursorPosition]) {
				setTimeout(() => {
					phoneRef.current && phoneRef.current.setSelectionRange(cursorPosition, cursorPosition);
				}, 0);
			}
			if (
				event.nativeEvent.inputType !== 'deleteContentBackward' &&
				value[cursorPosition] === ')'
			) {
				setTimeout(() => {
					phoneRef.current &&
						phoneRef.current.setSelectionRange(cursorPosition + 2, cursorPosition + 2);
				}, 0);
			}
			if (
				event.nativeEvent.inputType !== 'deleteContentBackward' &&
				(value[cursorPosition] === '(' || value[cursorPosition] === '-')
			) {
				setTimeout(() => {
					phoneRef.current &&
						phoneRef.current.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
				}, 0);
			}
			if (
				event.nativeEvent.inputType === 'deleteContentBackward' ||
				event.nativeEvent.inputType === 'deleteContentForward'
			) {
				setTimeout(() => {
					phoneRef.current && phoneRef.current.setSelectionRange(cursorPosition, cursorPosition);
				}, 0);
			}

			registration.phoneNumber = formattedPhoneNumber;

			if (registration.phoneNumber) {
				registration.isSMSConsentChecked = true;
				registration.isSMSConsentDisabled = false;
			} else {
				registration.isSMSConsentChecked = false;
				registration.isSMSConsentDisabled = true;
			}
		}

		setRegistrationForm({ ...registration });

		if (value) {
			setIsSubmitButtonDisabled(false);
		}
	};

	const zipCodeInputChange = (event: any) => {
		const { value } = event.target;
		setFormErrorMessages({ ...formErrorMessages, zipCode: '' });

		if (!value) {
			setRegistrationForm({ ...registrationForm, zipCode: '' });
		}
		if (value.length < 6 && isNumeric(value)) {
			setRegistrationForm({ ...registrationForm, zipCode: value });
		}
	};

	useEffect(() => {
		if (
			formErrorMessages.email ||
			formErrorMessages.firstName ||
			formErrorMessages.lastName ||
			formErrorMessages.password ||
			formErrorMessages.zipCode ||
			isEmailAlreadyInUse
		) {
			setIsSubmitButtonDisabled(true);
		} else {
			setIsSubmitButtonDisabled(false);
		}
	}, [
		formErrorMessages.email,
		formErrorMessages.firstName,
		formErrorMessages.lastName,
		formErrorMessages.password,
		formErrorMessages.zipCode,
		isEmailAlreadyInUse
	]);

	const handlePasswordOnBlur = (event: any) => {
		setIsPasswordTooltipOpened(false);
		validateFormInputsOnBlur(event);
	};

	const validateCreateAccountButtonClick = () => {
		if (!isCreateAccountButtonClicked) {
			setIsCreateAccountButtonClicked(true);
			return false;
		} else {
			return true;
		}
	};
	const validateFormInputsOnBlur = (event: any) => {
		const { name } = event.target;

		if (name === 'email') {
			const isEmailValid = checkEmailFormat(registrationForm.email);

			if (registrationForm.email && !isEmailValid) {
				setFormErrorMessages({
					...formErrorMessages,
					email: 'Please enter a valid email address.'
				});
			}
		}

		if (isFormInputValid) {
			const requiredFieldMessage = 'This is a required field.';
			const passwordValid = passwordStrengthValidation(registrationForm.password);

			if (name === 'zipCode') {
				if (registrationForm.zipCode && registrationForm.zipCode.length < 5) {
					setFormErrorMessages({ ...formErrorMessages, zipCode: 'Please enter a valid ZIP Code.' });
				}
			}

			if (!registrationForm.email && name === 'email') {
				setFormErrorMessages({ ...formErrorMessages, email: requiredFieldMessage });
			}

			if (name === 'password') {
				if (!registrationForm.password) {
					setFormErrorMessages({ ...formErrorMessages, password: requiredFieldMessage });
				} else if (registrationForm.password && !passwordValid.isAllCasesPassed) {
					setFormErrorMessages({
						...formErrorMessages,
						password: 'Password you entered does not match the requirements.'
					});
				}
			}
		}
	};

	const validateRegistrationForm = (): boolean => {
		let isFormValid = true;
		const isEmailValid = checkEmailFormat(registrationForm.email);
		const passwordValid = passwordStrengthValidation(registrationForm.password);
		setPasswordValid(passwordValid);

		const requiredFieldMessage = 'This is a required field.';

		if (registrationForm.phoneNumber && registrationForm.phoneNumber.length < 14) {
			formErrorMessages.phone = 'Please enter a valid phone number.';
			isFormValid = false;
			if (phoneRef.current !== null) {
				phoneRef.current.focus();
			}
		}

		if (registrationForm.zipCode && registrationForm.zipCode.length < 5) {
			formErrorMessages.zipCode = 'Please enter a valid ZIP Code.';
			isFormValid = false;
			if (zipCodeRef.current !== null) {
				zipCodeRef.current.focus();
			}
		}

		if (signUpType === 'TRADITIONAL') {
			if (!registrationForm.email) {
				formErrorMessages.email = requiredFieldMessage;
				isFormValid = false;
				if (emailRef.current !== null) {
					emailRef.current.focus();
				}
			}

			if (!registrationForm.password) {
				formErrorMessages.password = requiredFieldMessage;
				isFormValid = false;
				if (passwordRef.current !== null) {
					passwordRef.current.focus();
				}
			}

			if (registrationForm.email && !isEmailValid) {
				formErrorMessages.email = 'Please enter a valid email address.';
				isFormValid = false;
				if (emailRef.current !== null) {
					emailRef.current.focus();
				}
			}
			if (registrationForm.password && !passwordValid.isAllCasesPassed) {
				formErrorMessages.password = 'Password you entered does not match the requirements.';
				isFormValid = false;
				if (passwordRef.current !== null) {
					passwordRef.current.focus();
				}
			}
		} else if (signUpType === 'facebook') {
			if (!registrationForm.email) {
				formErrorMessages.email = requiredFieldMessage;
				isFormValid = false;
				if (emailRef.current !== null) {
					emailRef.current.focus();
				}
			}
			if (registrationForm.email && !isEmailValid) {
				formErrorMessages.email = 'Please enter a valid email address.';
				isFormValid = false;
				if (emailRef.current !== null) {
					emailRef.current.focus();
				}
			}
		}

		setFormErrorMessages(formErrorMessages);

		return isFormValid;
	};

	const handleSubmit = async (event: any) => {
		event.preventDefault();
		const isFormValid = validateRegistrationForm();
		setIsFormInputValid(validateCreateAccountButtonClick());

		if (isFormValid) {
			const captchaToken = await generateRecaptchaToken('vz_account_signup').then(
				(response: any) => response
			);

			setIsSubmitButtonDisabled(true);

			const registrationPayload: RegistrationPayload = {
				firstName: registrationForm.firstName || null,
				lastName: registrationForm.lastName || null,
				email: registrationForm.email,
				postalCode: registrationForm.zipCode || null,
				phoneNumber: registrationForm.phoneNumber || null,
				emailOptin: registrationForm.emailOptin,
				reCaptchaToken: captchaToken,
				password: ''
			};

			if (signUpType === 'TRADITIONAL') {
				registrationPayload.password = registrationForm.password;
			} else if (signUpType === 'google') {
				registrationPayload.googleToken = socialResponse.token;
			} else if (signUpType === 'facebook') {
				registrationPayload.facebookToken = socialResponse.token;
			} else if (signUpType === 'apple') {
				registrationPayload.appleToken = socialResponse.token;
			}

			callRegistrationService(registrationPayload);
		} else {
			setIsSubmitButtonDisabled(true);
		}
	};

	const callRegistrationService = (registrationPayload: RegistrationPayload) => {
		setIsSpinnerLoading(true);

		requestRedwolfService(accountAPI.createAccount(registrationPayload))
			.then((response: any) => {
				setIsSpinnerLoading(false);

				if (response.constructor.$metadata$.simpleName === 'Success' && response.data) {
					if (isSocialLogin) {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_VERIFY_ACCOUNT_SUCCESS');
					} else {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CREATE_ACCOUNT', { item_title: 'No' });
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_ACCOUNT_CREATION_SUCCESS');
					}

					const responseData = response.data;
					//TODO:(1) Review requiresCreateUser - Its not part of the accountAPI.login
					const userProfile: UserProfile = {
						firstName: responseData.user.firstName,
						lastName: responseData.user.lastName,
						email: responseData.user.email,
						userId: responseData.user.userId,
						signInType: signUpType,
						requiresCreateUser: responseData.requiresCreateUser,
						isUserLoggedIn: true,
						emailVerified: responseData.user.emailVerified,
						requiresEmailVerification: responseData.user.requiresEmailVerification,
						requiresPersonalization: responseData.user.requiresPersonalization
					};
					setToken(responseData.token);

					dispatch(setUserProfile({ userProfile, user: responseData.user }));
					setLocalStorage('isRequiresPersonalization', responseData.user.requiresPersonalization);
					setLocalStorage('requiresEmailVerification', responseData.user.requiresEmailVerification);
					dispatch(setTermsOfServiceStatus('UNKNOWN'));

					if (registrationForm.isSMSConsentChecked) {
						setLocalStorage('isSMSConsentChecked', true);
					}

					if (responseData.user.requiresEmailVerification === true) {
						navigate(`/confirm-email`);
					} else {
						navigate(`/terms-of-service?${queryStrings.toString()}`, { replace: true });
					}
				} else {
					if (
						response.response &&
						(response.response.status === 400 || response.response.status === 401) &&
						(response.response.detail === 'Email already exists.' ||
							response.response.detail ===
								'A unique attribute conflict was detected for attribute vz-email')
					) {
						setIsEmailAlreadyInUse(true);
						setIsSubmitButtonDisabled(false);
						if (
							emailRef.current &&
							formErrorMessages.firstName === '' &&
							formErrorMessages.lastName === ''
						) {
							emailRef.current.focus();
						}

						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CREATE_ACCOUNT', { item_title: 'Yes' });
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_ALREADY_EXISTS');
					} else {
						if (isSocialLogin) {
							logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_VERIFY_ACCOUNT_FAILURE', {
								error: response.response
							});
						} else {
							logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_ACCOUNT_CREATION_FAILURE');
						}
						if (response.response && response.response.status !== 400) {
							Sentry.captureException(new Error('registerNewUser:' + response));
						}
						if (
							response.response &&
							response.response.status === 400 &&
							response.response.detail === 'Email is invalid.'
						) {
							dispatch(
								openNotification({
									type: 'ERROR',
									heading: 'Error',
									body: 'Email is invalid. Please enter a valid email address.'
								})
							);
						} else {
							showTechnicalErrorMessage();
						}
					}

					setIsSpinnerLoading(false);
				}

				transaction && transaction.end();
			})
			.catch((err: any) => {
				showTechnicalErrorMessage();
				transaction && transaction.end();
				setIsSubmitButtonDisabled(false);
				setIsSpinnerLoading(false);
				Sentry.captureException(err);

				if (isSocialLogin) {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_VERIFY_ACCOUNT_FAILURE', { error: err });
				} else {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_ACCOUNT_CREATION_FAILURE');
				}
			});
	};

	if (firstNameRef.current && firstNameRef.current.value) {
		firstNameRef.current.classList.add('active');
	}
	if (lastNameRef.current && lastNameRef.current.value) {
		lastNameRef.current.classList.add('active');
	}
	if (emailRef.current && emailRef.current.value) {
		emailRef.current.classList.add('active');
	}
	if (zipCodeRef.current && zipCodeRef.current.value) {
		zipCodeRef.current.classList.add('active');
	}

	const passwordHideShow = (event: any) => {
		if (event.key === 'Enter') {
			setIsPasswordVisible(!isPasswordVisible);
		}
	};

	return (
		<section id="customer-register" className="account-create-contents">
			<div className="create-heading">
				{isSocialLogin ? (
					<>
						<h1 ref={headingRef} tabIndex={-1} aria-live="polite">
							Verify your VIZIO Account
						</h1>
						<p id="vizio-home-sub-title" className="vizio-home-sub-tittle">
							Please verify your contact information to continue.
						</p>
					</>
				) : (
					<h1 ref={headingRef} tabIndex={-1} aria-live="polite">
						Create your VIZIO Account
					</h1>
				)}
			</div>

			<form onSubmit={handleSubmit} className="create-account-form clearfix" noValidate>
				<div className="fieldset">
					<ul className="form-list">
						<li className={`fields ${formErrorMessages.firstName && 'has-error'}`}>
							<div className="field name-firstname form-group input-label">
								<span className="input-box">
									<input
										id="firstName"
										name="firstName"
										type="text"
										className="form-control input-text required-entry"
										autoComplete="given-name"
										aria-describedby="firstNameError"
										aria-invalid="false"
										value={registrationForm.firstName}
										onChange={handleInputChange}
										onBlur={validateFormInputsOnBlur}
										onKeyDown={handleKeyDown}
										ref={firstNameRef}
										spellCheck="false"
									/>
									<label htmlFor="firstName" className="control-label">
										First Name
									</label>

									<code id="firstNameError">{formErrorMessages.firstName}</code>
								</span>
							</div>
						</li>

						<li className={`fields ${formErrorMessages.lastName && 'has-error'}`}>
							<div className="field name-lastname form-group input-label">
								<span className="input-box">
									<input
										id="lastName"
										name="lastName"
										type="text"
										className="form-control input-text required-entry"
										autoComplete="family-name"
										aria-describedby="lastNameError"
										aria-invalid="false"
										value={registrationForm.lastName}
										onChange={handleInputChange}
										onBlur={validateFormInputsOnBlur}
										onKeyDown={handleKeyDown}
										ref={lastNameRef}
										spellCheck="false"
									/>
									<label htmlFor="lastName" className="control-label">
										Last Name
									</label>

									<code id="lastNameError">{formErrorMessages.lastName}</code>
								</span>
							</div>
						</li>

						<li className={`fields ${formErrorMessages.phone && 'has-error'}`}>
							<div className="input-box form-group input-label">
								<input
									type="text"
									className="form-control input-text required-entry"
									name="phoneNumber"
									id="signup-phone"
									autoComplete="off"
									aria-describedby="signup-phone-req"
									value={registrationForm.phoneNumber}
									onChange={handleInputChange}
									onBlur={validateFormInputsOnBlur}
									onKeyDown={handleKeyDown}
									maxLength={14}
									ref={phoneRef}
								/>
								<label htmlFor="signup-phone" className="control-label">
									Phone Number
								</label>
								<code id="signup-phone-req">{formErrorMessages.phone}</code>
							</div>
						</li>

						<li className={`fields ${formErrorMessages.zipCode && 'has-error'}`}>
							<div className="input-box form-group input-label">
								<input
									type="text"
									className="form-control input-text required-entry"
									id="zip-code"
									name="zipCode"
									maxLength={5}
									aria-describedby="zipcode-req"
									value={registrationForm.zipCode}
									onBlur={validateFormInputsOnBlur}
									onChange={zipCodeInputChange}
									ref={zipCodeRef}
								/>
								<label htmlFor="zip-code" className="control-label">
									ZIP Code
								</label>
								<code id="zipcode-req">{formErrorMessages.zipCode}</code>
							</div>
						</li>

						<li
							className={`fields ${
								formErrorMessages.email || isEmailAlreadyInUse ? 'has-error' : ''
							}`}>
							<div className="field">
								<div className="input-box form-group input-label">
									<input
										disabled={isEmailDisabled}
										id="email"
										name="email"
										type="text"
										className="form-control input-text validate-email required-entry login-field-transparent email-not-checked"
										autoComplete="email"
										aria-describedby="emailError emailAlreadyInUseError"
										aria-required="true"
										aria-invalid="false"
										value={registrationForm.email}
										onChange={handleInputChange}
										onBlur={validateFormInputsOnBlur}
										ref={emailRef}
										spellCheck="false"
									/>
									<label htmlFor="email" className="control-label">
										Email
									</label>

									<code
										id="emailError"
										className="signup-error-code"
										aria-atomic="true"
										aria-live="assertive">
										{formErrorMessages.email}
									</code>
									<code id="emailAlreadyInUseError" className="signup-error-code">
										{isEmailAlreadyInUse && (
											<span aria-live="polite">
												Email is already in use.{' '}
												<Link className="link" to="/login">
													Sign in.
												</Link>
											</span>
										)}
									</code>
								</div>
							</div>
						</li>
					</ul>
				</div>
				<div
					className="fieldset last"
					style={{ display: isPasswordFieldVisible ? 'block' : 'none' }}>
					<ul className="form-list">
						<li className={`fields ${formErrorMessages.password && 'has-error'}`}>
							<div className="field">
								<div
									className={`input-box form-group input-label ${isPasswordVisible && 'pw-view'}`}>
									<input
										id="password"
										name="password"
										type={isPasswordVisible ? 'text' : 'password'}
										className="form-control password input-text login-field-transparent required-entry validate-password"
										autoComplete="new-password"
										aria-describedby={`${
											isPasswordTooltipOpened
												? 'tooltipTitle tooltipList passwordError'
												: 'passwordError'
										}`}
										aria-required="true"
										aria-invalid="false"
										value={registrationForm.password}
										onBlur={handlePasswordOnBlur}
										onChange={handleInputChange}
										onFocus={() => setIsPasswordTooltipOpened(true)}
										ref={passwordRef}
									/>
									<label htmlFor="password" className="control-label">
										Password
									</label>

									<code id="passwordError">{formErrorMessages.password}</code>

									<div
										onClick={() => {
											setIsPasswordVisible(!isPasswordVisible);
										}}
										onKeyDown={(event) => passwordHideShow(event)}
										className="input-field-icon"
										aria-label={isPasswordVisible ? 'hide password' : 'show password'}
										tabIndex={0}
										role="button">
										<em aria-hidden="true" className="icon-eye eye-icon"></em>
									</div>

									<div
										className="pw-hint-block"
										id="password_hint"
										role="tooltip"
										style={{ display: isPasswordTooltipOpened ? 'block' : 'none' }}>
										<div className="psw-info-component">
											<div className="psw-info">
												<p className="psw-title" id="tooltipTitle">
													Please Include:
												</p>
												<ul className="checklist-info" id="tooltipList">
													<li
														className={
															passwordValid.isPasswordLengthPassed ? 'item' : 'item incomplete'
														}
														aria-hidden={passwordValid.isPasswordLengthPassed ? 'true' : 'false'}>
														8<span aria-label="to">-</span>25 characters
													</li>
													<li
														className={passwordValid.isLowercasePassed ? 'item' : 'item incomplete'}
														aria-hidden={passwordValid.isLowercasePassed ? 'true' : 'false'}>
														1 lowercase letter
													</li>
													<li
														className={passwordValid.isUppercasePassed ? 'item' : 'item incomplete'}
														aria-hidden={passwordValid.isUppercasePassed ? 'true' : 'false'}>
														1 uppercase letter
													</li>
													<li
														className={passwordValid.isNumerPassed ? 'item' : 'item incomplete'}
														aria-hidden={passwordValid.isNumerPassed ? 'true' : 'false'}>
														1 number
													</li>
												</ul>
											</div>
										</div>
									</div>
								</div>
							</div>
						</li>
					</ul>
				</div>

				<div className="agree-region check-cnt sign-up-check">
					<div className="input-row">
						<div className="form-checkbox CheckBox">
							<input
								id="signup-checkbox"
								name="signup-checkbox"
								type="checkbox"
								checked={registrationForm.isSMSConsentChecked}
								disabled={registrationForm.isSMSConsentDisabled}
								onChange={handleSMSConsentChange}
							/>
							<label className="checkbox-label" htmlFor="signup-checkbox">
								I agree to SMS messaging for VIZIO communications and advertisements, data and
								message rates may also apply{' '}
							</label>
						</div>
					</div>
				</div>

				<div className="register-buttons-set buttons-set form-buttons">
					<button
						type="submit"
						className="primary-button btn-create-account"
						title="Create Account"
						disabled={isSubmitButtonDisabled}>
						<span>{`${isSocialLogin ? 'Continue' : 'Create Account'}`}</span>
					</button>
				</div>
			</form>
			{isSpinnerLoading && <Spinner />}
		</section>
	);
};

export default SignUp;
